import { useState, useRef, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { watchQuillImageChanges } from '../utils/quillImageStyles';
import { watchImageHeight } from '../utils/imageHeightLimit';
import { processQuestionHtmlForRender, syncWidthsToDataAttr } from '../utils/questionHtmlProcessor';

interface QuillEditorWithImageResizeProps {
  value: string;
  onChange: (content: string, widths?: number[]) => void;
  questionId: string;
  imageWidths?: number[];
  onImageWidthsChange?: (widths: number[]) => void;
  /** 是否启用实时缩放样式（所见即所得模式） */
  applyRealtimeScaling?: boolean;
  /** 字段归属标记，用于隔离不同字段的图片缓存，防止串图 */
  targetField?: string; // 如 'content', 'scoringPoints', 'referenceAnswer'
}

interface ImageSizeState {
  scale: number;
  rect: DOMRect;
  display: 'inline' | 'block';
}

export default function QuillEditorWithImageResize({
  value,
  onChange,
  questionId,
  imageWidths = [],
  onImageWidthsChange,
  applyRealtimeScaling = false,
  targetField = 'content',
}: QuillEditorWithImageResizeProps) {
  const quillRef = useRef<ReactQuill>(null);
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState<ImageSizeState | null>(null);
  const [showSlider, setShowSlider] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  // 初始化 onChange 保护：ReactQuill 使用 defaultValue 非受控模式时，
  // 初始化时会触发一次 onChange（Quill 内部在设置内容后触发 text-change 事件）。
  // 忽略第一次 onChange，防止 QuillEditor 初始化导致父组件 isDirty 误判为 true。
  const isFirstChangeRef = useRef(true);
  const prevQuestionIdRef = useRef<string | null>(null);
  const cleanupObserverRef = useRef<(() => void) | null>(null);
  const unmountedRef = useRef(false);

  // 组件卸载时标记，防止异步回调访问已移除的 DOM
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const modules = {
    toolbar: [
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'color': [] }, { 'background': [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image', 'clean']
    ],
    clipboard: { matchVisual: false },
    history: { delay: 500, maxStack: 100, userOnly: true }
  };

  const formats = [
    'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'align', 'link', 'image'
  ];

  // 非受控模式：questionId变化时（切换题目）或首次加载时，重新设置编辑器内容
  // defaultValue只在首次挂载时生效，需要手动同步内容
  // 注意：由于 ReactQuill 使用 key={questionId} 强制重建，此 useEffect 主要处理
  // 同一 questionId 下组件未重建但需要同步内容的场景
  // 本组件为纯显示组件：value 由调用方决定是否规范化（v1 兜底规范化，v2 原样保留）
  // 这里只做 processQuestionHtmlForRender（无损变换：URL 规范化、空段清理、width% 注入）
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 首次加载或切换题目时同步内容
    if (prevQuestionIdRef.current !== questionId) {
      // 标记正在程序化更新，防止 onChange 回传导致 isDirty 误判
      isResizingRef.current = true;
      // 先清空编辑器内容，防止残留旧题目的图片
      editor.setText('');
      const htmlWithWidths = processQuestionHtmlForRender(value, imageWidths);
      editor.clipboard.dangerouslyPasteHTML(htmlWithWidths || '<p></p>');
      prevQuestionIdRef.current = questionId;
      setSelectedImage(null);
      setShowSlider(false);
      // 延迟重置标记，确保 dangerouslyPasteHTML 触发的 onChange 不回传
      setTimeout(() => { isResizingRef.current = false; }, 0);
    }
  }, [questionId]);

  // 监听 value 变化（取消修改场景）：当外部 value 与编辑器当前内容不一致时，
  // 直接通过 DOM innerHTML 赋值更新内容，避免 setText+pasteHTML 的清空-填充闪烁
  useEffect(() => {
    if (prevQuestionIdRef.current !== questionId) return; // 首次加载由上面的 effect 处理
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const htmlWithWidths = processQuestionHtmlForRender(value, imageWidths);
    const currentHtml = editor.root.innerHTML;

    // 内容不一致时才更新，避免不必要的重渲染
    if (htmlWithWidths && htmlWithWidths !== currentHtml) {
      // 标记正在程序化更新，防止 onChange 回传导致循环
      isResizingRef.current = true;
      // 直接通过 innerHTML 赋值，避免 setText('') 清空再 pasteHTML 的视觉闪烁
      // Quill 的 root.innerHTML 赋值不会触发 Quill 的内部状态更新，但 DOM 会立即更新
      editor.root.innerHTML = htmlWithWidths || '<p></p>';
      // 延迟重置标记，确保 onChange 不触发
      setTimeout(() => { isResizingRef.current = false; }, 0);
    }
  }, [value, imageWidths]);

  // 当imageWidths外部变化时（非缩放操作导致的），应用到DOM
  // 当imageWidths为空数组时，使用默认值100%
  useEffect(() => {
    if (isResizingRef.current) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const images = editor.root.querySelectorAll('img');
    if (images.length === 0) return;

    images.forEach((img, index) => {
      const width = index < imageWidths.length ? imageWidths[index] : 100;
      img.style.width = `${width}%`;
      // 不设置 maxWidth = '100%'，避免覆盖 imageHeightLimit 设置的 maxWidth 约束（规则2）
      img.style.height = 'auto';
    });
  }, [imageWidths]);

  // 实时缩放模式：使用 MutationObserver 监听 DOM 变化并自动应用图片样式
  useEffect(() => {
    if (!applyRealtimeScaling) {
      // 如果关闭实时缩放，清理之前的 observer
      if (cleanupObserverRef.current) {
        cleanupObserverRef.current();
        cleanupObserverRef.current = null;
      }
      return;
    }

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 清理之前的 observer
    if (cleanupObserverRef.current) {
      cleanupObserverRef.current();
    }

    // 启动新的 observer
    cleanupObserverRef.current = watchQuillImageChanges(editor.root, imageWidths);

    // 组件卸载时清理
    return () => {
      if (cleanupObserverRef.current) {
        cleanupObserverRef.current();
        cleanupObserverRef.current = null;
      }
    };
  }, [applyRealtimeScaling, imageWidths]);

  // 图片高度限制：基于容器宽度动态计算 max-height
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 启动 ResizeObserver 监听编辑器容器大小变化
    const cleanup = watchImageHeight(editor.root);

    // 组件卸载或 questionId 变化时清理
    return () => {
      cleanup();
    };
  }, [questionId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      try {
        const target = e.target as HTMLElement;
        const isInEditor = target.closest('.quill-editor-wrapper');
        const isInSlider = target.closest('.image-size-slider');
        const isImage = target.tagName === 'IMG';
        if (isImage) return;
        if (isInEditor && !isImage) {
          setSelectedImage(null);
          setShowSlider(false);
          document.querySelectorAll('.ql-editor img').forEach((img) => img.classList.remove('selected'));
          return;
        }
        if (!isInEditor && !isInSlider) {
          setSelectedImage(null);
          setShowSlider(false);
          document.querySelectorAll('.ql-editor img').forEach((img) => img.classList.remove('selected'));
        }
      } catch {
        // 组件卸载期间 DOM 已被移除，静默忽略
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const editorElement = editor.root;

    const handleImageClick = (e: Event) => {
      const target = e.target as HTMLElement;
      let imgElement: HTMLImageElement | null = null;

      if (target.tagName === 'IMG') {
        imgElement = target as HTMLImageElement;
      } else {
        const clickX = (e as MouseEvent).clientX;
        const clickY = (e as MouseEvent).clientY;
        const allImages = editorElement.querySelectorAll('img');
        let closestImg: HTMLImageElement | null = null;
        let minDistance = Infinity;
        allImages.forEach((img) => {
          const rect = img.getBoundingClientRect();
          const imgCenterX = rect.left + rect.width / 2;
          const imgCenterY = rect.top + rect.height / 2;
          const distance = Math.sqrt(Math.pow(clickX - imgCenterX, 2) + Math.pow(clickY - imgCenterY, 2));
          if (clickX >= rect.left - 10 && clickX <= rect.right + 10 &&
              clickY >= rect.top - 10 && clickY <= rect.bottom + 10) {
            if (distance < minDistance) {
              minDistance = distance;
              closestImg = img as HTMLImageElement;
            }
          }
        });
        imgElement = closestImg;
      }

      if (!imgElement) return;
      e.preventDefault();
      e.stopPropagation();

      setShowSlider(false);
      setSelectedImage(null);

      setTimeout(() => {
        // 组件已卸载时跳过，避免访问已移除的 DOM 节点导致 Uncaught 异常
        if (unmountedRef.current) return;
        try {
          editorElement.querySelectorAll('img').forEach((img) => img.classList.remove('selected'));
          imgElement.classList.add('selected');
          setSelectedImage(imgElement);

          // 从DOM style获取当前宽度
          const currentWidth = parseInt(imgElement.style.width) || 100;
          const computedStyle = window.getComputedStyle(imgElement);
          const display = computedStyle.display === 'block' ? 'block' : 'inline';
          const rect = imgElement.getBoundingClientRect();
          setImageSize({ scale: currentWidth, rect, display: display as 'inline' | 'block' });
          setInputValue(String(currentWidth));
          setShowSlider(true);
        } catch {
          // DOM 节点已被移除，静默忽略
        }
      }, 10);
    };

    editorElement.addEventListener('click', handleImageClick, true);
    return () => { editorElement.removeEventListener('click', handleImageClick, true); };
  }, [questionId]);

  useEffect(() => {
    if (!showSlider || !selectedImage) return;
    const updatePosition = () => {
      try {
        const rect = selectedImage.getBoundingClientRect();
        setImageSize(prev => prev ? { ...prev, rect } : null);
      } catch {
        // 组件卸载期间 DOM 节点已被移除，静默忽略
      }
    };
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSlider, selectedImage]);

  // 拖拽滑块时：直接修改DOM样式（视觉反馈）
  // 非受控模式下，DOM修改不会被ReactQuill重新渲染覆盖
  const handleScaleChange = useCallback((newScale: number) => {
    if (selectedImage) {
      isResizingRef.current = true;
      selectedImage.style.width = `${newScale}%`;
      selectedImage.style.maxWidth = '100%';
      selectedImage.style.height = 'auto';
      setImageSize(prev => prev ? { ...prev, scale: newScale } : null);
      setInputValue(String(newScale));
    }
  }, [selectedImage]);

  // 手动输入框值变化时：实时同步滑块和图片宽度
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
    const num = parseInt(val);
    if (!isNaN(num) && num >= 10 && num <= 100 && selectedImage) {
      isResizingRef.current = true;
      selectedImage.style.width = `${num}%`;
      selectedImage.style.maxWidth = '100%';
      selectedImage.style.height = 'auto';
      setImageSize(prev => prev ? { ...prev, scale: num } : null);
    }
  }, [selectedImage]);

  // 松开滑块时：合并title和titleImageWidths为单次调用，避免React异步状态更新覆盖
  const handleScaleChangeComplete = useCallback(() => {
    if (!isResizingRef.current) return;

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 从DOM中提取所有img的当前宽度
    const allImages = editor.root.querySelectorAll('img');
    const newWidths: number[] = [];
    allImages.forEach((img) => {
      const w = parseInt(img.style.width) || 100;
      newWidths.push(w);
    });

    // 获取当前HTML，统一处理（与 quiz 预览模式走同一份函数）
    // 关键：调用 syncWidthsToDataAttr 把 style 中的 width 同步到 data-edit-w（存储源）
    // 这样保存到数据库的 HTML 自带 data-edit-w，quiz 渲染时直接读取，无需外部 widths 数组
    const currentHtml = editor.root.innerHTML;
    const htmlWithDataAttr = syncWidthsToDataAttr(currentHtml);
    const htmlWithWidths = processQuestionHtmlForRender(htmlWithDataAttr, newWidths);

    isResizingRef.current = false;
    onChange(htmlWithWidths, newWidths);
  }, [onChange]);

  // 输入框失焦时：确认最终值并持久化
  const handleInputBlur = useCallback(() => {
    const num = parseInt(inputValue);
    if (isNaN(num) || num < 10 || num > 100) {
      const currentScale = imageSize?.scale || 100;
      setInputValue(String(currentScale));
      return;
    }
    handleScaleChangeComplete();
  }, [inputValue, imageSize, handleScaleChangeComplete]);

  // ReactQuill的onChange：原样透传 content，不做任何 HTML 改写
  // 原因：Quill 是非受控组件，onChange 回调返回的 content 是 Quill 内部 model 的快照。
  // 如果在这里做"空段清理 / <br> 压缩 / 图文分行"等改写，会破坏 Quill 内部状态：
  //   1. 阶段2 会移除 Quill 维护的空 <p></p>（光标占位符）
  //   2. 阶段4 会在每次输入时插入 <br>，Quill 收到改写 HTML 后会重新规范化
  //   3. 最终导致"用户输入"与"保存内容"不一致，看起来修改未持久化
  // 所有改写只在两个时机做：
  //   - defaultValue 加载时（编辑器初始化展示）
  //   - handleScaleChangeComplete 拖拽完成时（需要把 width% 写回 HTML）
  // 渲染一致性由 quiz 端的 processQuestionHtmlForRender 保证（两端走同一份函数）
  const handleChange = (content: string) => {
    if (isResizingRef.current) return;
    // 忽略初始化时 Quill 触发的第一次 onChange，防止父组件 isDirty 误判
    if (isFirstChangeRef.current) {
      isFirstChangeRef.current = false;
      return;
    }
    onChange(content);
  };

  // 非受控模式：defaultValue 在首次挂载时生效
  // 由于 ReactQuill 使用 key={questionId_targetField} 强制重建，每次切换题目或字段都会重新挂载
  // 同时 useEffect 中也会在 questionId 变化时先清空再设置内容，双重保障防止图片残留
  // 使用 targetField 生成隔离的缓存 key，防止不同字段的图片互相串图
  // 本组件为纯显示组件：value 由调用方决定是否规范化（v1 兜底规范化，v2 原样保留）
  // 这里只做 processQuestionHtmlForRender（无损变换：URL 规范化、空段清理、width% 注入）
  const editorKey = `${questionId}_${targetField}`;
  const processedDefaultValue = processQuestionHtmlForRender(value, imageWidths);

  // 在 Quill 编辑器挂载后，手动添加 undo/redo 按钮
  useEffect(() => {
    // 延迟执行，确保 Quill 工具栏完全渲染
    const timer = setTimeout(() => {
      // 直接通过 DOM 查找工具栏容器，避免依赖 Quill 内部 API
      const wrapper = containerRef.current?.querySelector('.quill-editor-wrapper');
      if (!wrapper) return;

      const toolbarContainer = wrapper.querySelector('.ql-toolbar');
      if (!toolbarContainer) return;

      // 检查是否已存在 undo/redo 按钮，避免重复添加
      if (toolbarContainer.querySelector('.custom-undo-btn')) return;

      // undo SVG 图标（与 Quill 内置按钮风格一致的 18x18 viewBox）
      const undoSvg = `<svg viewBox="0 0 18 18"><path d="M5.5 7.5L2 11l3.5 3.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 11h9.5c2.5 0 4.5-2 4.5-4.5S14 2 11.5 2H8" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      // redo SVG 图标
      const redoSvg = `<svg viewBox="0 0 18 18"><path d="M12.5 7.5L16 11l-3.5 3.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 11H6.5C4 11 2 9 2 6.5S4 2 6.5 2H10" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      // 创建 undo 按钮 - 使用自定义类名避免被 Quill toolbar 当作 format 处理
      const undoButton = document.createElement('button');
      undoButton.type = 'button';
      undoButton.className = 'custom-undo-btn';
      undoButton.setAttribute('title', '撤销');
      undoButton.innerHTML = undoSvg;
      undoButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const quillInstance = quillRef.current?.getEditor();
        if (quillInstance && quillInstance.history) {
          quillInstance.history.undo();
        }
      });

      // 创建 redo 按钮
      const redoButton = document.createElement('button');
      redoButton.type = 'button';
      redoButton.className = 'custom-redo-btn';
      redoButton.setAttribute('title', '重做');
      redoButton.innerHTML = redoSvg;
      redoButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const quillInstance = quillRef.current?.getEditor();
        if (quillInstance && quillInstance.history) {
          quillInstance.history.redo();
        }
      });

      // 将按钮插入到工具栏最后面（其他工具的右侧），顺序：重做、撤销
      toolbarContainer.appendChild(redoButton);
      toolbarContainer.appendChild(undoButton);
    }, 200);

    return () => clearTimeout(timer);
  }, [editorKey]);

  return (
    <div ref={containerRef} className="relative">
      <div className="quill-editor-wrapper">
        <ReactQuill
          key={editorKey}
          ref={quillRef}
          defaultValue={processedDefaultValue}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder="请输入题目内容，支持富文本格式..."
          className="bg-white"
        />
      </div>

      {showSlider && imageSize && selectedImage && (
        <div
          ref={sliderRef}
          className="image-size-slider"
          style={{
            position: 'fixed',
            left: `${Math.max(10, Math.min(imageSize.rect.left + imageSize.rect.width / 2 - 120, window.innerWidth - 260))}px`,
            top: `${Math.max(10, imageSize.rect.bottom + 10)}px`,
            zIndex: 9999
          }}
        >
          <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg min-w-[240px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">图片缩放</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="10"
                  max="100"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onBlur={handleInputBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInputBlur(); } }}
                  className="w-12 h-5 text-xs text-center border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
            </div>
            {/* 快捷缩放按钮 */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {[20, 30, 40, 50, 60, 70, 80, 90, 99].map((preset) => (
                <button
                  key={preset}
                  onClick={() => {
                    const newScale = preset;
                    // 修改DOM样式（视觉反馈）
                    selectedImage.style.width = `${newScale}%`;
                    selectedImage.style.maxWidth = '100%';
                    selectedImage.style.height = 'auto';
                    setImageSize(prev => prev ? { ...prev, scale: newScale } : null);
                    setInputValue(String(newScale));

                    // 直接执行持久化逻辑
                    const editor = quillRef.current?.getEditor();
                    if (!editor) return;
                    const allImages = editor.root.querySelectorAll('img');
                    const newWidths: number[] = [];
                    allImages.forEach((img) => {
                      const w = parseInt(img.style.width) || 100;
                      newWidths.push(w);
                    });
                    const currentHtml = editor.root.innerHTML;
                    // 同步 data-edit-w（存储源），保证保存到数据库的 HTML 自带尺寸
                    const htmlWithDataAttr = syncWidthsToDataAttr(currentHtml);
                    const htmlWithWidths = processQuestionHtmlForRender(htmlWithDataAttr, newWidths);
                    onChange(htmlWithWidths, newWidths);
                  }}
                  className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                    imageSize.scale === preset
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <input
              type="range"
              min="10"
              max="100"
              value={imageSize.scale}
              onChange={(e) => handleScaleChange(parseInt(e.target.value))}
              onMouseUp={handleScaleChangeComplete}
              onTouchEnd={handleScaleChangeComplete}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${imageSize.scale - 10}%, #e5e7eb ${imageSize.scale - 10}%, #e5e7eb 100%)`
              }}
            />
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>10</span>
              <span>100</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}